import { create } from 'zustand';
import { APP_CONFIG, isDevOpen } from '../app/config';

// ─── Session Store ─────────────────────────────────────────────────────────────
// Single source of truth for auth/session across all three modes.
// Axios client reads token from here — not directly from localStorage.

// Maps backend roles array (["end_user","admin","super_admin"]) to a role string
// the existing UI understands ('user' | 'admin').
function mapBackendRoles(rolesArray) {
  if (!Array.isArray(rolesArray) || rolesArray.length === 0) return 'user';
  if (rolesArray.includes('super_admin') || rolesArray.includes('admin')) return 'admin';
  if (rolesArray.includes('operator')) return 'operator';
  return 'user';
}

function isSuperAdminFromRoles(rolesArray) {
  return Array.isArray(rolesArray) && rolesArray.includes('super_admin');
}

// Normalise backend UserProfileResponse to the internal user shape { id, username, name, email }
function normalizeUser(backendUser) {
  if (!backendUser) return null;
  return {
    id:       backendUser.id,
    username: backendUser.username,
    // full_name is the backend field; name is what the UI uses
    name:     backendUser.full_name || backendUser.name || backendUser.username || '',
    email:    backendUser.email     || null,
    phone:    backendUser.phone     || null,
    roles:    backendUser.roles     || [],
    status:   backendUser.status    || null,
    email_verified: backendUser.email_verified ?? false,
    must_change_password: backendUser.must_change_password ?? false,
  };
}

function loadFromStorage() {
  try {
    return {
      user:         JSON.parse(localStorage.getItem('algosys_user')        || 'null'),
      token:        localStorage.getItem('algosys_token')                  || null,
      role:         localStorage.getItem('algosys_role')                   || 'user',
      isSuperAdmin: localStorage.getItem('algosys_is_super_admin') === '1',
      mustChangePassword: localStorage.getItem('algosys_must_change_password') === '1',
    };
  } catch {
    return { user: null, token: null, role: 'user', isSuperAdmin: false, mustChangePassword: false };
  }
}

function getInitialState() {
  const mode = APP_CONFIG.sessionMode;

  // dev-open: use devUser from config, no token needed
  if (mode === 'dev-open') {
    const { id, username, name, role } = APP_CONFIG.devUser;
    return {
      mode,
      user:         { id, username, name, email: null, phone: null, roles: [], status: null, email_verified: false },
      role,
      isSuperAdmin: role === 'super_admin',
      token:        null,
      embedded:     false,
      ready:        true,
    };
  }

  // standalone-token: read from localStorage
  if (mode === 'standalone-token') {
    const stored = loadFromStorage();
    return {
      mode,
      user:         stored.user,
      role:         stored.role,
      isSuperAdmin: stored.isSuperAdmin,
      mustChangePassword: stored.mustChangePassword,
      token:        stored.token,
      embedded:     false,
      // ready=false when token exists but user not yet loaded (will be bootstrapped via /auth/me)
      ready:        !stored.token || !!stored.user,
    };
  }

  // algosys-embedded: not ready until parent bootstraps
  if (mode === 'algosys-embedded') {
    return {
      mode,
      user:         null,
      role:         'user',
      isSuperAdmin: false,
      mustChangePassword: false,
      token:        null,
      embedded:     true,
      ready:        false,
    };
  }

  return { mode: 'dev-open', user: null, role: 'user', isSuperAdmin: false, mustChangePassword: false, token: null, embedded: false, ready: false };
}

export const useSessionStore = create((set, get) => ({
  ...getInitialState(),

  // Current workspace being viewed: 'user' | 'admin'
  // Admins default to admin workspace; operators default to user (trading) workspace
  workspace: getInitialState().role === 'admin' ? 'admin' : 'user',
  setWorkspace: (ws) => set({ workspace: ws }),

  // Disclaimer is accepted once per token (per fresh login).
  // Reload preserves acceptance; logout + new login forces re-acceptance.
  disclaimerAccepted:
    !!localStorage.getItem('algosys_token')
    && localStorage.getItem('algosys_disclaimer_token') === localStorage.getItem('algosys_token'),
  acceptDisclaimer: () => {
    const tok = get().token;
    if (tok) localStorage.setItem('algosys_disclaimer_token', tok);
    set({ disclaimerAccepted: true });
  },

  // Called from standalone login form or after /auth/me bootstrap
  // Accepts either a roles array (from backend) or a role string (legacy).
  setSession: ({ user: rawUser, token, role: roleStr, roles: rolesArray, mustChangePassword }) => {
    const user = normalizeUser(rawUser);
    const resolvedRolesArray = rolesArray || rawUser?.roles || [];
    const role = rolesArray ? mapBackendRoles(rolesArray) : (roleStr || 'user');
    const superAdmin = isSuperAdminFromRoles(resolvedRolesArray);
    const forcePasswordChange = mustChangePassword ?? rawUser?.must_change_password ?? false;

    localStorage.setItem('algosys_user',           JSON.stringify(user));
    localStorage.setItem('algosys_token',          token);
    localStorage.setItem('algosys_role',           role);
    localStorage.setItem('algosys_is_super_admin', superAdmin ? '1' : '0');
    localStorage.setItem('algosys_must_change_password', forcePasswordChange ? '1' : '0');

    const acceptedForThisToken = localStorage.getItem('algosys_disclaimer_token') === token;
    set({
      user, token, role, isSuperAdmin: superAdmin, mustChangePassword: forcePasswordChange, ready: true,
      workspace: role === 'admin' ? 'admin' : 'user',
      disclaimerAccepted: acceptedForThisToken,
    });
  },

  // Called from Algosys parent frame via postMessage
  bootstrapFromParent: ({ user: rawUser, token, role: roleStr, roles: rolesArray }) => {
    const user = normalizeUser(rawUser);
    const role = rolesArray ? mapBackendRoles(rolesArray) : (roleStr || 'user');
    const superAdmin = isSuperAdminFromRoles(rolesArray || rawUser?.roles || []);
    set({
      user, token, role, isSuperAdmin: superAdmin, ready: true, embedded: true,
      workspace: role === 'admin' ? 'admin' : 'user',
    });
  },

  clearSession: () => {
    localStorage.removeItem('algosys_user');
    localStorage.removeItem('algosys_token');
    localStorage.removeItem('algosys_role');
    localStorage.removeItem('algosys_is_super_admin');
    localStorage.removeItem('algosys_must_change_password');
    localStorage.removeItem('algosys_disclaimer_token');
    set({ user: null, token: null, role: 'user', isSuperAdmin: false, mustChangePassword: false, ready: true, workspace: 'user', disclaimerAccepted: false });
  },

  // Helpers
  isAdmin:    () => ['admin', 'both'].includes(get().role),
  canAdmin:   () => APP_CONFIG.features.adminEnabled && ['admin', 'operator', 'both'].includes(get().role),
  getToken:   () => get().token,
}));

// ─── Convenience selectors ─────────────────────────────────────────────────────
export const getToken = () => useSessionStore.getState().getToken();
export const isReady  = () => useSessionStore.getState().ready;
